import Form from "views/Form";
import className from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { Globals } from "utils/styleconfig";
import { RiArrowRightSLine } from "react-icons/ri";
import { createSearchParams, useNavigate, useParams } from "react-router-dom";
import { useEffect, useLayoutEffect, useState } from "react";
import { setNavbarTitle } from "features/navbar/navbarSlice";
import FormElement from "components/Form/FormElement/FormElement";
import { formConfig } from "pages/Extensions/EditExtension/formConfig";
import FormDropdown from "components/Form/FormDropdown/FormDropdown";
import { searchHandler } from "models/search";
import { selectAllResults } from "features/search/searchSlice";
import { dropdownItemSelector } from "models/form";
import { selectAuthUser } from "features/auth/authSlice";
import { actions, parseError } from "utils/helpers";
import { toast } from "react-hot-toast";
import {
  useGetExtensionQuery,
  useUpdateExtensionMutation,
} from "features/extension/extensionApiSlice";

import cardStyles from "styles/modules/Card.module.scss";
import formStyles from "components/Form/Form.module.scss";
import utilityStyles from "styles/modules/Utility.module.scss";

const form = new Form(formConfig);

const EditExtension = ({ label }) => {
  const params = useParams();

  const authUser = useSelector(selectAuthUser);

  const [activeInput, setActiveInput] = useState("");

  const { data: extension } = useGetExtensionQuery(params?.id);

  const searchResults = useSelector(selectAllResults);

  const [formElements, setFormElements] = useState([]);
  const [formValidity, setFormValidity] = useState(true);
  const [isChangesMade, setIsChangesMade] = useState(false);
  const [initialDataValues, setInitialDataValues] = useState([]);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [updateExtension, { isLoading }] = useUpdateExtensionMutation();

  useEffect(() => {
    dispatch(setNavbarTitle("Edit Extension"));
  }, [dispatch]);

  useLayoutEffect(() => {
    form.createFormElements();
    setFormElements(form.formElements);
  }, []);

  useEffect(() => {
    if (extension) {
      const dataValues = [];
      const updatedFormConfig = JSON.parse(JSON.stringify(formConfig));

      Object.entries(extension).forEach(([key, value]) => {
        if (updatedFormConfig[key]) {
          if (key === "caller_id" && extension.account_caller_id) {
            updatedFormConfig[key].elementConfig.value =
              extension.account_caller_id.callerid_number;
            updatedFormConfig[key].elementConfig["data-id"] = value;
          } else if (key === "account_id" && extension.account) {
            updatedFormConfig[key].elementConfig.value =
              extension.account.number;
            updatedFormConfig[key].elementConfig["data-id"] = value;
          } else {
            updatedFormConfig[key].elementConfig.value = value;
          }
          dataValues.push(String(value));
        }
        return null;
      });

      form.setConfig(updatedFormConfig);
      form.createFormElements();
      setFormElements(form.formElements);
      setInitialDataValues(dataValues);
    }
  }, [extension]);

  useEffect(() => {
    return () => {
      if (process.env.NODE_ENV === "production") {
        form.clear(); // will run once in production
        form.setConfig(formConfig);
      }
    };
  }, []);

  const onViewDevicesBtnHandler = () => {
    navigate(`/extensions/${params.id}/devices`);
  };

  const submitFormHandler = async (e) => {
    e.preventDefault();

    const formData = {};

    if (formValidity && authUser?.actions[label]?.includes(actions.update)) {
      for (const key in form.getFormConfig()) {
        const el = form.getFormConfig()[key];

        if (key === "caller_id" || key === "account_id") {
          formData[key] = el.elementConfig["data-id"]; // Send the ID instead of the displayed value
        } else {
          formData[key] = el.elementConfig.value;
        }

      }

      try {
        await updateExtension({
          extensionId: extension.id,
          ...formData,
        }).unwrap();
        toast.success("Extension updated successfully");
        navigate({
          pathname: "/extensions",
          search: createSearchParams({ page: "1" }).toString(),
        });
        window.location.reload();
      } catch (error) {
        const errorMessage = parseError(error);
        toast.error(errorMessage);
      }
    }
  };

  const dropdownItemClickHandler = (key, item) => {
    if (key === "caller_id") {
      form.updateFormElement({
        id: key,
        config: {
          ...form.getFormConfig()[key],
          elementConfig: {
            ...form.getFormConfig()[key].elementConfig,
            value: item.callerid_number, // Display value
            "data-id": item.id, // Store ID
          },
        },
      });
    } else if (key === "account_id") {
      form.updateFormElement({
        id: key,
        config: {
          ...form.getFormConfig()[key],
          elementConfig: {
            ...form.getFormConfig()[key].elementConfig,
            value: item.number, // Display value
            "data-id": item.id, // Store ID
          },
        },
      });
    } else {
      dropdownItemSelector(key, item, form);
    }

    setFormElements(form.formElements);
    setActiveInput("");
  };


  const inputChangeHandler = (e) => {
    const updatedInput = form.inputChangeHandler(e);

    setActiveInput(e.target.id);

    if (updatedInput) {
      let isFormChanged = true;
      const updatedFormValues = [];

      updatedFormValues.push(e.target.value);

      if (e.target.id === "did_id") {
        searchHandler("/dids", e.target.value, dispatch);
      }

      if (e.target.id === "caller_id") {
        searchHandler("/callerids", e.target.value, dispatch);
      }

      if (e.target.id === "account_id") {
        searchHandler("/accounts", e.target.value, dispatch);
      }

      form.updateFormElement({ id: e.target.id, config: updatedInput });

      form.checkFormValidity();

      isFormChanged =
        updatedFormValues.every((value) => initialDataValues.includes(value)) &&
        isFormChanged;

      setFormElements(form.formElements);
      setFormValidity(form.isValid);
      setIsChangesMade(!isFormChanged);
    }
  };

  let buttonEl = null;

  if (authUser?.actions[label].includes(actions.update)) {
    buttonEl = (
      <div className={formStyles.btn}>
        <button disabled={isLoading || !formValidity || !isChangesMade}>
          {isLoading ? "Saving..." : "Save Changes"}
        </button>
      </div>
    );
  }

  return (
    <div
      className={`${utilityStyles.flexColumn} ${utilityStyles.itemsCenter} ${utilityStyles.justifyCenter}`}
    >
      <div
        className={className({
          [cardStyles.menu]: true,
          [utilityStyles.wFull]: true,
        })}
      >
        <h3>Changes made to this extension will overwrite the existing info</h3>
      </div>
      <div className={formStyles.wrapper}>
        <form className={formStyles.container} onSubmit={submitFormHandler}>
          {formElements.map((el, idx) => {
            const { title, elementType, elementConfig, ...config } = el.config;

            return (
              <div key={`el-group-${idx + 1}`} className={formStyles.group}>
                <FormElement
                  {...config}
                  title={title}
                  type={elementType}
                  id={elementConfig.id}
                  config={{
                    ...elementConfig,
                    onChange: inputChangeHandler,
                  }}
                />
                <FormDropdown
                  items={searchResults}
                  itemClickHandler={(item) =>
                    dropdownItemClickHandler(elementConfig.id, item)
                  }
                  visible={
                    activeInput === elementConfig.id && el.config.dropdown
                  }
                />
              </div>
            );
          })}
          {buttonEl}
        </form>
      </div>
    </div>
  );
};

export default EditExtension;
